import {useQuery} from 'react-query'
import {useAuth} from '../../auth'
import {getConfigById} from '../components/configs-list/core/_requests'
import {QUERIES} from '../../../../_metronic/helpers'

/**
 * Hook para verificar se o usuário atual pode deletar tarefas
 * Retorna true se:
 * - A configuração only_manager_admin_delete_tasks estiver desativada (padrão), OU
 * - A configuração estiver ativada E o usuário for gestor ou administrador, OU
 * - A configuração estiver ativada E allow_creator_delete_own_tasks estiver ativada E o usuário for o criador da tarefa
 */
export function useCanDeleteTask(task?: { creator_id?: string | number }) {
  const {currentUser} = useAuth()
  const instanceId = currentUser?.instance_id

  const {data: config, isLoading} = useQuery(
    `${QUERIES.PROJECTS_LIST}-config-${instanceId}`,
    () => {
      if (!instanceId) return null
      return getConfigById(instanceId)
    },
    {
      enabled: !!instanceId,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  // Se não há configuração ou está carregando, permite por padrão (comportamento atual)
  if (isLoading || !config?.data) {
    return {canDelete: true, isLoading: true, config: config?.data}
  }

  const onlyManagerAdminDeleteTasks = config.data.only_manager_admin_delete_tasks || false

  // Se a configuração está desativada, todos podem deletar
  if (!onlyManagerAdminDeleteTasks) {
    return {canDelete: true, isLoading: false, config: config.data}
  }

  // Se a configuração está ativada, verifica se é gestor ou admin
  // Boolean() aceita tanto boolean quanto número (1/0) que podem vir do backend
  const isManager = Boolean(currentUser?.manager)
  const isAdmin = Boolean(currentUser?.administrator)
  const canDeleteByRole = isManager || isAdmin

  // Se já pode deletar por role, retorna true
  if (canDeleteByRole) {
    return {canDelete: true, isLoading: false, config: config.data}
  }

  // Se não pode por role, verifica se pode deletar por ser o criador
  const allowCreatorDeleteOwnTasks = config.data.allow_creator_delete_own_tasks || false
  if (allowCreatorDeleteOwnTasks && task?.creator_id) {
    const currentUserId = currentUser?.id
    const taskCreatorId = typeof task.creator_id === 'string' 
      ? parseInt(task.creator_id) 
      : task.creator_id
    
    const isCreator = currentUserId === taskCreatorId
    return {canDelete: isCreator, isLoading: false, config: config?.data}
  }

  return {canDelete: false, isLoading: false, config: config?.data}
}
